import {
  GROUPING_GRID_NO_GROUPS,
  TABLE_PAGE_SIZE_UNGROUPED,
} from "../../constants";
import type { IGroupValue } from "../../models/group-value.models";
import type {
  IGroupResult,
  IPaginatedResult,
  IPaginationChangeEvent,
  ISearchCriteriaGroupValue,
  ITakeSkip,
} from "../../models/pagination.models";
import { safeNumber } from "../numbers.helpers";

export type {
  IPaginationChangeEvent,
  ISearchCriteriaGroupValue,
  ITakeSkip,
} from "../../models/pagination.models";

export const isPaginatedResult = (
  data: unknown,
): data is IPaginatedResult<unknown[]> => {
  return (
    typeof data === "object" &&
    data !== null &&
    "items" in data &&
    Array.isArray((data as IPaginatedResult<unknown>).items) &&
    "count" in data &&
    typeof (data as IPaginatedResult<unknown>).count === "number"
  );
};

export const isGroupResult = (
  data: unknown,
): data is IGroupResult<unknown[]>[] => {
  return (
    Array.isArray(data) &&
    data.length > 0 &&
    typeof data[0] === "object" &&
    data[0] !== null &&
    "groupData" in data[0] &&
    Array.isArray((data[0] as IGroupResult<unknown>).groupData)
  );
};

/**
 * Once an ungrouped page has loaded, every page click arrives as an array holding the grid's pager under
 * `GROUPING_GRID_NO_GROUPS`. A `null` value is a grouped grid's blank group, which pages under another key.
 */
const isUngroupedGridGroupValue = (value: IGroupValue["value"]): boolean =>
  value === GROUPING_GRID_NO_GROUPS;

/**
 * Flat list search may still receive composed pagination as an array
 * (one entry per visible group). Per-group page clicks use `groupPagination`; otherwise
 * pick the ungrouped slot or reset to the first page.
 */
export const resolveFlatListTakeSkip = ({
  paginationResult,
  groupPagination,
  resetToFirstPage = false,
  pageSize = TABLE_PAGE_SIZE_UNGROUPED,
}: {
  paginationResult: ITakeSkip | ISearchCriteriaGroupValue[];
  groupPagination?: IPaginationChangeEvent;
  resetToFirstPage?: boolean;
  pageSize?: number;
}): ITakeSkip => {
  if (groupPagination) {
    const { pageIndex, pageSize: eventPageSize } = groupPagination.pagination;

    return {
      take: eventPageSize,
      skip: pageIndex * eventPageSize,
    };
  }

  if (resetToFirstPage) {
    return {
      take: pageSize,
      skip: 0,
    };
  }

  if (!Array.isArray(paginationResult)) {
    return paginationResult;
  }

  const ungroupedPage = paginationResult.find(page =>
    isUngroupedGridGroupValue(page.value),
  );

  if (ungroupedPage) {
    return {
      take: ungroupedPage.take,
      skip: ungroupedPage.skip,
    };
  }

  return {
    take: pageSize,
    skip: 0,
  };
};

/** Server groups partition the rows (blank values form the `null` group), so their counts sum to the total. */
export const sumGroupValueCounts = (
  groupValues: readonly IGroupValue[],
): number =>
  groupValues.reduce((total, { count }) => total + safeNumber(count), 0);

/** Moves a grid's server total by the rows added or removed locally; it never drops below zero. */
export const shiftRowsCount = ({
  count,
  delta,
}: {
  count: number;
  delta: number;
}): number => Math.max(0, count + delta);
